// Create Test — builds a timed mock test from the published PYQ bank
// (chapters + difficulty + question count + duration), instead of the old
// free-form AI quiz generator (topic/context text -> LLM). The generated
// test is published immediately (real bank content, no draft review), so
// this page's only job is to hand off to the attempt page, which shows the
// "Start Test" popup and runs the timer.
import React, { useEffect, useState } from 'react';
import { AccentRule } from './studentUi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardList, Sparkles, ArrowLeft } from 'lucide-react';
import { InlineSparkle } from '../../components/Loader';
import api from './studentApi';
import { showEarnedToasts } from './BadgeUnlockToast';
import { getExamPrepColors } from './examPrepTheme';
import { useStudent } from './StudentApp';
import { notifyError } from '../../lib/errors';

const DIFFICULTIES = [
  { value: 'mixed', label: 'Mixed (random)' },
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' },
];

export default function CreateQuizPage() {
  const navigate = useNavigate();
  const { me, theme } = useStudent();
  const examPrep = getExamPrepColors(theme?.id);
  const targetExam = me?.student_profile?.target_exam || '';

  const [taxonomyYear, setTaxonomyYear] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [chapters, setChapters] = useState([]);
  const [selectedChapters, setSelectedChapters] = useState(() => new Set());
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [difficulty, setDifficulty] = useState('mixed');
  const [count, setCount] = useState(10);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!targetExam) return;
    let alive = true;
    api.get(`/exam-taxonomy/${targetExam}/current`)
      .then(d => { if (alive) { setTaxonomyYear(d.year); setSubjects(d.subjects || []); } })
      .catch(() => { if (alive) { setTaxonomyYear(null); setSubjects([]); } });
    return () => { alive = false; };
  }, [targetExam]);

  // Subject picked -- fetch its chapters. Chapters already selected under a
  // different subject stay selected (a mock test can span subjects); only
  // the visible checklist changes to whichever subject is active.
  useEffect(() => {
    setChapters([]);
    if (!targetExam || !taxonomyYear || !subjectId) return;
    let alive = true;
    api.get(`/exam-taxonomy/${targetExam}/${taxonomyYear}/children/${subjectId}`)
      .then(d => alive && setChapters(d || [])).catch(() => alive && setChapters([]));
    return () => { alive = false; };
  }, [subjectId, taxonomyYear, targetExam]);

  const toggleChapter = (chapterId) => {
    setSelectedChapters(prev => {
      const next = new Set(prev);
      if (next.has(chapterId)) next.delete(chapterId); else next.add(chapterId);
      return next;
    });
  };

  const generate = async () => {
    const duration = Number(durationMinutes);
    if (!Number.isFinite(duration) || duration < 5 || duration > 240) {
      toast.error('Duration must be between 5 and 240 minutes.');
      return;
    }
    const questionCount = Number(count);
    if (!Number.isFinite(questionCount) || questionCount < 1 || questionCount > 50) {
      toast.error('Questions must be between 1 and 50.');
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post('/student/exam-prep/tests', {
        chapter_node_ids: [...selectedChapters],
        difficulty,
        count: Number(count),
        duration_minutes: Number(durationMinutes),
      });
      toast.success(`Test ready — ${(res.questions || []).length} questions, ${res.time_limit} minutes.`);
      showEarnedToasts(res.newly_earned_badges);
      navigate(`/student/quizzes/${res.id}`);
    } catch (e) {
      notifyError(e, 'Could not generate test');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="create-quiz-page">
      <button onClick={() => navigate('/student/quizzes')} className="text-xs font-semibold inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft className="w-4 h-4" /> Back to Practice
      </button>

      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: examPrep.goldTint, color: examPrep.gold }}>
          <ClipboardList className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-semibold opacity-60">Exam Prep</div>
          <h1 className="text-3xl font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>
            Create a test
          </h1>
<AccentRule className="mt-2" />
        </div>
      </header>

      {!targetExam ? (
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <p className="text-sm" style={{ color: 'var(--st-mute)' }}>Set your target exam in Settings first.</p>
        </div>
      ) : (
        <div className="rounded-2xl border p-5 space-y-4" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div>
            <label className="text-xs font-semibold opacity-60 block mb-1.5">Duration (minutes)</label>
            <input
              type="number" min={5} max={240} value={durationMinutes}
              onChange={e => setDurationMinutes(e.target.value)}
              data-testid="create-quiz-duration-input"
              className="w-full rounded-xl px-3.5 py-2.5 text-sm outline-none"
              style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold opacity-60 block mb-1.5">Difficulty</label>
              <select value={difficulty} onChange={e => setDifficulty(e.target.value)}
                data-testid="create-quiz-difficulty-select"
                className="w-full rounded-xl px-3.5 py-2.5 text-sm outline-none"
                style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }}>
                {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold opacity-60 block mb-1.5">Questions</label>
              <input type="number" min={1} max={50} value={count}
                onChange={e => setCount(e.target.value)}
                data-testid="create-quiz-count-input"
                className="w-full rounded-xl px-3.5 py-2.5 text-sm outline-none"
                style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold opacity-60 block mb-1.5">Subject</label>
            <select value={subjectId} onChange={e => setSubjectId(e.target.value)}
              data-testid="create-quiz-subject-select"
              className="w-full rounded-xl px-3.5 py-2.5 text-sm outline-none"
              style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)', color: 'var(--st-ink)' }}>
              <option value="">Select a subject…</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {subjectId && (
            <div>
              <label className="text-xs font-semibold opacity-60 block mb-1.5">
                Chapters {selectedChapters.size > 0 && `(${selectedChapters.size} selected)`}
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--st-mute)' }}>Leave none selected to draw from every chapter.</p>
              <div className="rounded-xl border max-h-64 overflow-y-auto p-3 space-y-1.5" style={{ borderColor: 'var(--st-border)' }} data-testid="create-quiz-chapter-list">
                {chapters.length === 0 && (
                  <p className="text-xs" style={{ color: 'var(--st-mute)' }}>Loading chapters…</p>
                )}
                {chapters.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--st-ink)' }}>
                    <input type="checkbox" checked={selectedChapters.has(c.id)} onChange={() => toggleChapter(c.id)}
                      data-testid={`create-quiz-chapter-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <button onClick={generate} disabled={generating} data-testid="create-quiz-generate-button"
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: examPrep.gold, color: '#101826' }}>
            {generating ? <><InlineSparkle size={20} label="Generating test" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate test</>}
          </button>
        </div>
      )}
    </div>
  );
}
